import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Clock, Users, Heart, ArrowRight, Repeat } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_CONTENT = {
  hero_headline: "Your time is valuable. Share it wisely.",
  hero_subheadline: "Join a community where an hour of help equals an hour of help — no matter the skill. Exchange tutoring for gardening, cooking lessons for tech support, and more.",
  hero_cta_primary: "Start Exchanging",
  hero_cta_secondary: "I have an account",
  stat1_value: "500+", stat1_label: "Active Members",
  stat2_value: "2,000+", stat2_label: "Hours Exchanged",
  stat3_value: "50+", stat3_label: "Skill Categories",
  footer_text: "Built with care for community.",
};

export function LandingPage() {
  const [content, setContent] = useState(DEFAULT_CONTENT);

  useEffect(() => {
    axios.get(`${API_URL}/api/site-content`)
      .then(res => setContent({ ...DEFAULT_CONTENT, ...res.data }))
      .catch(() => {});
  }, []);

  // Split headline so we can highlight the second sentence with the yellow background
  const headlineParts = (() => {
    const h = content.hero_headline || '';
    const idx = h.lastIndexOf('.');
    if (idx > 0 && idx < h.length - 1) {
      return { first: h.slice(0, idx + 1), second: h.slice(idx + 1).trim() };
    }
    return { first: h, second: '' };
  })();

  const features = [
    {
      icon: Clock,
      title: 'Time-Based Currency',
      description: 'Every hour of service equals one time credit, regardless of the skill shared.'
    },
    {
      icon: Users,
      title: 'Community Powered',
      description: 'Connect with neighbors and community members to exchange skills and services.'
    },
    {
      icon: Repeat,
      title: 'Give & Receive',
      description: 'Offer your skills when you can, request help when you need it.'
    },
    {
      icon: Heart,
      title: 'Build Connections',
      description: 'Strengthen community bonds while getting things done together.'
    }
  ];

  const howItWorks = [
    { step: '01', title: 'Create Your Profile', description: 'Sign up and list the skills you can offer to your community.' },
    { step: '02', title: 'Browse or Post', description: 'Find help you need or offer your skills on the community board.' },
    { step: '03', title: 'Connect & Exchange', description: 'Arrange exchanges via your preferred contact method.' },
    { step: '04', title: 'Log & Rate', description: 'Record completed exchanges and build your reputation.' }
  ];

  return (
    <div className="min-h-screen">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#fef9e7] border-b-3 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <Link to="/" className="flex items-center gap-2" data-testid="landing-logo">
              <div className="w-10 h-10 rounded-md bg-[#ffd703] border-2 border-black flex items-center justify-center">
                <Clock className="w-6 h-6 text-black" />
              </div>
              <span className="font-bold text-xl text-black font-sans hidden sm:inline">New Community Wealth Incubator</span>
              <span className="font-bold text-lg text-black font-sans sm:hidden">NCWI</span>
            </Link>
            <div className="flex items-center gap-3">
              <Link to="/login">
                <button className="retro-btn retro-btn-outline px-4 py-2 text-sm" data-testid="landing-login-btn">
                  Sign In
                </button>
              </Link>
              <Link to="/register">
                <button className="retro-btn px-4 py-2 text-sm" data-testid="landing-register-btn">
                  Get Started
                </button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-16 lg:py-24 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="retro-card p-8 md:p-12 relative">
            {/* Window header */}
            <div className="retro-card-header absolute top-0 left-0 right-0 rounded-t-none">
              <div className="window-dots">
                <div className="window-dot window-dot-green"></div>
                <div className="window-dot window-dot-lavender"></div>
                <div className="window-dot window-dot-yellow"></div>
                <div className="window-dot window-dot-pink"></div>
              </div>
              <span className="sparkle animate-sparkle">✦</span>
              <span className="font-sans font-semibold text-black">welcome</span>
            </div>
            
            <div className="mt-12 text-center max-w-3xl mx-auto">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-black mb-6 leading-tight">
                {headlineParts.first}{headlineParts.second && (
                  <> <span className="bg-[#ffd703] px-2">{headlineParts.second}</span></>
                )}
              </h1>
              <p className="text-lg text-black/80 mb-8 leading-relaxed max-w-2xl mx-auto">
                {content.hero_subheadline}
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/register">
                  <button className="retro-btn px-8 py-3 text-lg flex items-center gap-2" data-testid="hero-cta">
                    {content.hero_cta_primary}
                    <ArrowRight className="w-5 h-5" />
                  </button>
                </Link>
                <Link to="/login">
                  <button className="retro-btn retro-btn-outline px-8 py-3 text-lg" data-testid="hero-login">
                    {content.hero_cta_secondary}
                  </button>
                </Link>
              </div>
              
              {/* Stats */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-8 mt-12 pt-8 border-t-2 border-black">
                <div className="text-center">
                  <p className="text-3xl font-bold text-black font-sans" data-testid="stat-1-value">{content.stat1_value}</p>
                  <p className="text-sm text-black/60 font-sans">{content.stat1_label}</p>
                </div>
                <div className="hidden sm:block w-px h-12 bg-black"></div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-black font-sans" data-testid="stat-2-value">{content.stat2_value}</p>
                  <p className="text-sm text-black/60 font-sans">{content.stat2_label}</p>
                </div>
                <div className="hidden sm:block w-px h-12 bg-black"></div>
                <div className="text-center">
                  <p className="text-3xl font-bold text-black font-sans" data-testid="stat-3-value">{content.stat3_value}</p>
                  <p className="text-sm text-black/60 font-sans">{content.stat3_label}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Example Story Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="retro-card p-8 relative">
            <div className="retro-card-header absolute top-0 left-0 right-0">
              <div className="window-dots">
                <div className="window-dot window-dot-green"></div>
                <div className="window-dot window-dot-lavender"></div>
                <div className="window-dot window-dot-yellow"></div>
                <div className="window-dot window-dot-pink"></div>
              </div>
              <span className="sparkle animate-sparkle">✦</span>
              <span className="font-sans font-semibold text-black">example</span>
            </div>
            
            <div className="mt-8 text-lg leading-relaxed text-black">
              <p className="mb-4">
                You are a sign painter with a steady hand and an eye for color, and one afternoon you restore a little bakery's weathered sign to something worth looking at — four hours of work, four time credits in your account.
              </p>
              <p className="mb-4">
                You take those credits across town to a mechanic who knows engines the way you know brushstrokes, and drive home with a car that finally runs right.
              </p>
              <p>
                The mechanic spends their credits with a seamstress down the street, who lets out the waist on their favorite pants and sends them home feeling like themselves again. No one pulled out a wallet, no one went without — just neighbors trading what they're good at, one hour at a time.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-black mb-4">
              Built for <span className="bg-[#e056a0] text-white px-2">Community</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature) => (
              <div key={feature.title} className="retro-card p-6">
                <div className="w-12 h-12 rounded-md bg-[#ffd703] border-2 border-black flex items-center justify-center mb-4">
                  <feature.icon className="w-6 h-6 text-black" />
                </div>
                <h3 className="text-lg font-bold text-black mb-2 font-sans">{feature.title}</h3>
                <p className="text-sm text-black/70 font-sans leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="retro-card p-8 md:p-12 relative">
            <div className="retro-card-header absolute top-0 left-0 right-0">
              <div className="window-dots">
                <div className="window-dot window-dot-green"></div>
                <div className="window-dot window-dot-lavender"></div>
                <div className="window-dot window-dot-yellow"></div>
                <div className="window-dot window-dot-pink"></div>
              </div>
              <span className="sparkle animate-sparkle">✦</span>
              <span className="font-sans font-semibold text-black">how it works</span>
            </div>
            
            <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {howItWorks.map((item) => (
                <div key={item.step} className="relative">
                  <div className="text-6xl font-bold text-[#e056a0]/30 mb-2 font-sans">{item.step}</div>
                  <h3 className="text-lg font-bold text-black mb-2 font-sans">{item.title}</h3>
                  <p className="text-sm text-black/70 font-sans">{item.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="retro-card p-8 md:p-12 bg-[#d8b4fe] text-black text-center relative">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 text-black">
              Ready to join the community?
            </h2>
            <p className="text-black/80 mb-8 max-w-xl mx-auto font-sans">
              Start exchanging time credits today. Your skills are valuable — share them with others who need them.
            </p>
            <Link to="/register">
              <button className="retro-btn px-8 py-3 text-lg flex items-center gap-2 mx-auto" data-testid="cta-register">
                Create Your Account
                <ArrowRight className="w-5 h-5" />
              </button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 bg-[#fef9e7] border-t-3 border-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-md bg-[#ffd703] border-2 border-black flex items-center justify-center">
                <Clock className="w-4 h-4 text-black" />
              </div>
              <span className="font-bold text-black font-sans">NCWI</span>
            </div>
            <p className="text-sm text-black/60 font-sans text-center md:text-right" data-testid="footer-text">
              {content.footer_text} © {new Date().getFullYear()} New Community Wealth Incubator.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
